import * as React from "react";
import { ComboBox, InputMode, TextField } from "../../../ui";
import { EditVehicleValues } from "../editVehicleForm.utils";
import { TouchContext } from "../touchContext";
import { ListPaper } from "./ListPaper";
import { Option, fieldId, labelOf, valueOf } from "../editVehicleField.utils";

export const Field: React.FC<{
  field: keyof EditVehicleValues;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  inputMode?: InputMode;
  options?: Option[];
  multiline?: boolean;
  required?: boolean;
  placeholder?: string;
}> = ({
  field,
  label,
  value,
  onChange,
  error,
  inputMode = InputMode.TEXT,
  options,
  multiline,
  required,
  placeholder,
}) => {
  const touch = React.useContext(TouchContext);
  // An id ties the label to its input, so screen readers name every field.
  const id = fieldId(field);
  const common = {
    id,
    label,
    error: !!error,
    helperText: error,
    required,
    placeholder,
    fullWidth: true,
    shrinkLabel: true,
  };

  if (options && !touch) {
    const selected = options.find((option) => valueOf(option) === value) ?? null;
    return (
      <ComboBox<Option>
        {...common}
        options={options}
        value={selected}
        onChange={(next) => onChange(next ? valueOf(next) : "")}
        getOptionLabel={labelOf}
        isOptionEqual={(option, current) => valueOf(option) === valueOf(current)}
        // Required lists cannot be cleared to nothing; optional ones can.
        disableClearable={required}
        autoHighlight
        listComponent={ListPaper}
      />
    );
  }

  return (
    <TextField
      {...common}
      value={value}
      onChange={onChange}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      select={!!options}
      nativeSelect={!!options}
      // The number pad for numbers on a phone; a desktop keyboard ignores it.
      inputMode={options ? undefined : inputMode}
    >
      {options && (
        <>
          {/* A required list cannot be set back to nothing, but while empty it must say so, not show its first entry. */}
          {(!required || !value) && (
            <option value="" disabled={required}>
              {required ? "Choose…" : "Not set"}
            </option>
          )}
          {options.map((option) => (
            <option key={valueOf(option)} value={valueOf(option)}>
              {labelOf(option)}
            </option>
          ))}
        </>
      )}
    </TextField>
  );
};
